/**
 * SpectateCamera — drives camera target/position when the local player is
 * eliminated (BR) or freezes on a killer for a kill-cam beat (free + BR).
 *
 * The active mode is owned here; `CameraController` queries `getMode()`
 * and `getDesiredTarget()` once per frame and lerps toward whatever this
 * class returns. When mode is `'follow'`, the controller falls back to
 * its own swarm-follow logic.
 */

import type { Vec3 } from '../../shared/utils';

export type CameraMode = 'follow' | 'spectate' | 'killcam';

export type PlayerId = string;

export interface SpectateLookup {
  /**
   * Returns a snapshot of the world state used to resolve targets:
   * - the lead drone position for a player (used by spectate)
   * - any drone position for a player (used by kill-cam)
   * - the killer's username (used for the caption)
   */
  getPlayerLeadPosition(playerId: PlayerId): Vec3 | null;
  getPlayerAnyPosition(playerId: PlayerId): Vec3 | null;
  getPlayerName(playerId: PlayerId): string | null;
}

export interface SpectateCallbacks {
  /** Called when killcam expires; lets GameClient transition to spectate / respawn. */
  onKillCamEnd?: () => void;
  /** Caption updates for HUD ("KILLED BY: <name>" or empty to hide). */
  onCaption?: (text: string) => void;
}

const DEFAULT_TARGET: Vec3 = { x: 0, y: 20, z: 0 };

export class SpectateCamera {
  private mode: CameraMode = 'follow';
  private survivors: PlayerId[] = [];
  private survivorIndex = 0;
  private lastDesiredTarget: Vec3 = { ...DEFAULT_TARGET };

  private killCamTargetId: PlayerId | null = null;
  private killCamRemainingMs = 0;

  constructor(
    private readonly lookup: SpectateLookup,
    private readonly callbacks: SpectateCallbacks = {},
  ) {}

  getMode(): CameraMode {
    return this.mode;
  }

  /**
   * Mode-aware target. CameraController lerps toward this each frame when
   * mode is `spectate` or `killcam`. Returns null when the controller
   * should keep its own follow target.
   */
  getDesiredTarget(): Vec3 | null {
    if (this.mode === 'follow') return null;
    if (this.mode === 'killcam') {
      const pos = this.killCamTargetId
        ? this.lookup.getPlayerAnyPosition(this.killCamTargetId)
        : null;
      if (pos) this.lastDesiredTarget = pos;
      return this.lastDesiredTarget;
    }
    // spectate
    const pid = this.survivors[this.survivorIndex];
    const pos = pid ? this.lookup.getPlayerLeadPosition(pid) : null;
    if (pos) this.lastDesiredTarget = pos;
    return this.lastDesiredTarget;
  }

  enterSpectate(survivors: PlayerId[]): void {
    this.survivors = survivors.slice();
    this.survivorIndex = 0;
    this.mode = this.survivors.length > 0 ? 'spectate' : 'follow';
    if (this.mode === 'spectate') this.callbacks.onCaption?.('');
  }

  cycleSpectate(direction: 1 | -1): void {
    if (this.mode !== 'spectate' || this.survivors.length === 0) return;
    const n = this.survivors.length;
    this.survivorIndex = (this.survivorIndex + direction + n) % n;
  }

  exitSpectate(): void {
    if (this.mode === 'killcam') return;
    this.mode = 'follow';
    this.survivors = [];
    this.survivorIndex = 0;
    this.callbacks.onCaption?.('');
  }

  triggerKillCam(killerId: PlayerId, durationMs: number): void {
    this.mode = 'killcam';
    this.killCamTargetId = killerId;
    this.killCamRemainingMs = Math.max(0, durationMs);
    const name = this.lookup.getPlayerName(killerId);
    this.callbacks.onCaption?.(name ? `KILLED BY: ${name}` : 'KILLED');
  }

  update(deltaMs: number): void {
    if (this.mode !== 'killcam') return;
    this.killCamRemainingMs -= deltaMs;
    if (this.killCamRemainingMs > 0) return;

    this.killCamRemainingMs = 0;
    this.killCamTargetId = null;
    this.mode = 'follow';
    this.callbacks.onCaption?.('');
    this.callbacks.onKillCamEnd?.();
  }

  dispose(): void {
    this.mode = 'follow';
    this.survivors = [];
    this.killCamTargetId = null;
    this.killCamRemainingMs = 0;
  }
}
